import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner, TextInput } from './Controls.tsx';
import { EmptyState } from './EmptyState.tsx';
import { ErrorAlert } from './ErrorAlert.tsx';
import { useFormatters } from '../hooks/useFormatters.ts';

export interface DirectoryColumn<T> {
    key: string;
    label: string;
    render: (item: T) => ReactNode;
}

/** Shared searchable, paginated directory with explicit loading/error/empty states. */
export function DirectoryTable<T extends { id: string }>({
    columns,
    items,
    total,
    page,
    pageSize,
    onPageChange,
    search,
    onSearchChange,
    loading,
    error,
}: {
    columns: DirectoryColumn<T>[];
    items: T[];
    total: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    search: string;
    onSearchChange: (search: string) => void;
    loading: boolean;
    error: unknown;
}) {
    const { t } = useTranslation();
    const format = useFormatters();
    const pageCount = Math.ceil(total / pageSize);
    const pages = [...new Set([1, ...Array.from({ length: 5 }, (_, index) => page + index - 2), pageCount])]
        .filter((value) => value >= 1 && value <= pageCount)
        .sort((a, b) => a - b);
    return (
        <div className="flex flex-col gap-4">
            <TextInput
                type="search"
                label={t('platform.directory.search')}
                value={search}
                onChange={(event) => onSearchChange(event.currentTarget.value)}
            />
            {error ? (
                <ErrorAlert error={error} />
            ) : loading ? (
                <div className="flex justify-center p-8">
                    <Spinner label={t('platform.shell.loading')} />
                </div>
            ) : (
                <>
                    <p className="text-sm text-muted" role="status">
                        {t('platform.directory.count', { count: total })}
                    </p>
                    {items.length === 0 ? (
                        <EmptyState
                            title={t('platform.directory.empty')}
                            description={t('platform.directory.emptyHelp')}
                        />
                    ) : (
                        <div
                            className="overflow-x-auto rounded-xl border border-line bg-surface shadow-xs"
                            role="region"
                            aria-label={t('platform.directory.results')}
                            tabIndex={0}
                        >
                            <table className="w-full min-w-[40rem] border-collapse text-left text-sm">
                                <thead className="border-b border-line bg-surface-muted">
                                    <tr>
                                        {columns.map((column) => (
                                            <th
                                                scope="col"
                                                key={column.key}
                                                className="px-4 py-3 text-xs font-semibold tracking-wide text-muted uppercase"
                                            >
                                                {column.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-line">
                                    {items.map((item) => (
                                        <tr key={item.id} className="transition-colors hover:bg-surface-muted/60">
                                            {columns.map((column) => (
                                                <td key={column.key} className="px-4 py-3">
                                                    {column.render(item)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {total > pageSize && (
                        <nav
                            aria-label={t('platform.directory.pagination')}
                            className="flex flex-wrap items-center justify-end gap-1"
                        >
                            <Button
                                variant="default"
                                size="xs"
                                disabled={page <= 1}
                                onClick={() => onPageChange(page - 1)}
                            >
                                {t('platform.directory.previous')}
                            </Button>
                            {pages.map((value, index) => (
                                <span key={value} className="inline-flex items-center gap-1">
                                    {index > 0 && value > (pages[index - 1] ?? 0) + 1 && (
                                        <span aria-hidden="true">{t('platform.directory.ellipsis')}</span>
                                    )}
                                    <Button
                                        variant={value === page ? 'primary' : 'default'}
                                        size="xs"
                                        aria-current={value === page ? 'page' : undefined}
                                        aria-label={t('platform.directory.page', { page: value })}
                                        onClick={() => onPageChange(value)}
                                    >
                                        {format.number(value)}
                                    </Button>
                                </span>
                            ))}
                            <Button
                                variant="default"
                                size="xs"
                                disabled={page >= pageCount}
                                onClick={() => onPageChange(page + 1)}
                            >
                                {t('platform.directory.next')}
                            </Button>
                        </nav>
                    )}
                </>
            )}
        </div>
    );
}
